/**
 * URL Priorities Table Component
 *
 * Displays URL-level AEO priorities with score breakdown,
 * filtering, and export capabilities.
 */

import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger
} from '@/components/ui/tooltip';
import {
  Target, Download, ExternalLink, Bot, Percent, TrendingUp,
  ChevronLeft, ChevronRight, Eye, EyeOff, Info, Search, X
} from 'lucide-react';

import {
  getUrlPriorities,
  exportPriorities,
  downloadCsv,
  addManualExclusion,
  UrlPriority,
  PrioritySummary,
} from '@/services/aeoPrioritiesService';

interface Props {
  siteId: string;
}

export function UrlPrioritiesTable({ siteId }: Props) {
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [priorities, setPriorities] = useState<UrlPriority[]>([]);
  const [summary, setSummary] = useState<PrioritySummary | null>(null);
  const [page, setPage] = useState(0);
  const [limit] = useState(25);
  const [excludeEnriched, setExcludeEnriched] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [categories, setCategories] = useState<string[]>([]);
  const [includePattern, setIncludePattern] = useState('');
  const [excludePattern, setExcludePattern] = useState('');

  useEffect(() => {
    loadPriorities();
  }, [siteId, page, excludeEnriched, categoryFilter]);

  const loadPriorities = async () => {
    setLoading(true);
    try {
      const result = await getUrlPriorities(siteId, {
        limit,
        offset: page * limit,
        excludeEnriched,
        category: categoryFilter !== 'all' ? categoryFilter : undefined,
      });
      setPriorities(result.priorities);
      setSummary(result.summary);

      // Extract unique categories
      const uniqueCategories = [...new Set(result.priorities
        .map(p => p.context.category)
        .filter((c): c is string => c !== null)
      )];
      if (uniqueCategories.length > 0 && categories.length === 0) {
        setCategories(uniqueCategories);
      }
    } catch (err) {
      console.error('Failed to load URL priorities:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await exportPriorities(siteId, 'url');
      downloadCsv(blob, `aeo-url-priorities-${siteId}.csv`);
    } catch (err) {
      console.error('Failed to export:', err);
    } finally {
      setExporting(false);
    }
  };

  const handleExcludeUrl = async (urlPath: string) => {
    try {
      await addManualExclusion(siteId, urlPath, 'Excluded from UI');
      loadPriorities();
    } catch (err) {
      console.error('Failed to exclude URL:', err);
    }
  };

  const getScoreColor = (score: number): string => {
    if (score >= 70) return 'bg-green-500';
    if (score >= 40) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const formatUrl = (url: string): string => {
    if (url.length > 60) {
      return url.substring(0, 57) + '...';
    }
    return url;
  };

  // Convert wildcard pattern (with *) to regex for URL matching
  const matchesPattern = (url: string, pattern: string): boolean => {
    if (!pattern.trim()) return true;
    const escapedPattern = pattern
      .replace(/[.+?^${}()|[\]\\]/g, '\\$&') // Escape regex special chars except *
      .replace(/\*/g, '.*'); // Convert * to .*
    const regex = new RegExp(escapedPattern, 'i');
    return regex.test(url);
  };

  // Filter priorities based on include/exclude patterns
  const filteredPriorities = priorities.filter(p => {
    // If include pattern is set, URL must match it
    if (includePattern.trim() && !matchesPattern(p.url, includePattern)) {
      return false;
    }
    // If exclude pattern is set, URL must NOT match it
    if (excludePattern.trim() && matchesPattern(p.url, excludePattern)) {
      return false;
    }
    return true;
  });

  if (loading && priorities.length === 0) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Target className="h-5 w-5" />
              URL Priorities
            </CardTitle>
            <CardDescription className="mt-1">
              URLs ranked by potential AEO impact. Higher scores = higher priority for enrichment.
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            disabled={exporting}
          >
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>

        {/* Filters Row 1 */}
        <div className="flex items-center gap-4 mt-4">
          <div className="flex items-center gap-2">
            <Checkbox
              id="excludeEnriched"
              checked={excludeEnriched}
              onCheckedChange={(checked) => setExcludeEnriched(checked === true)}
            />
            <label htmlFor="excludeEnriched" className="text-sm cursor-pointer">
              Hide already enriched
            </label>
          </div>

          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="All categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {categories.map(cat => (
                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {summary && (
            <div className="ml-auto text-sm text-muted-foreground">
              {summary.includedUrls.toLocaleString()} URLs
              {summary.excludedUrls > 0 && ` (${summary.excludedUrls.toLocaleString()} excluded)`}
            </div>
          )}
        </div>

        {/* Filters Row 2 - URL Pattern Filters */}
        <div className="flex items-center gap-4 mt-3">
          <div className="flex items-center gap-2 flex-1">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="relative flex-1 max-w-xs">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Include pattern (e.g. /en/*)"
                      value={includePattern}
                      onChange={(e) => setIncludePattern(e.target.value)}
                      className="pl-9 pr-8"
                    />
                    {includePattern && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="absolute right-1 top-1 h-6 w-6"
                        onClick={() => setIncludePattern('')}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Only show URLs matching this pattern</p>
                  <p className="text-xs text-muted-foreground">Use * as wildcard</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          <div className="flex items-center gap-2 flex-1">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="relative flex-1 max-w-xs">
                    <EyeOff className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Exclude pattern (e.g. /es/*)"
                      value={excludePattern}
                      onChange={(e) => setExcludePattern(e.target.value)}
                      className="pl-9 pr-8"
                    />
                    {excludePattern && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="absolute right-1 top-1 h-6 w-6"
                        onClick={() => setExcludePattern('')}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Hide URLs matching this pattern</p>
                  <p className="text-xs text-muted-foreground">Use * as wildcard</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          {(includePattern || excludePattern) && (
            <div className="text-sm text-muted-foreground">
              Showing {filteredPriorities.length} of {priorities.length}
            </div>
          )}
        </div>
      </CardHeader>

      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[40%]">URL</TableHead>
              <TableHead className="w-[100px] text-center">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger className="flex items-center gap-1 justify-center">
                      Score
                      <Info className="h-3 w-3" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <p className="font-semibold mb-1">Score Components:</p>
                      <ul className="text-xs space-y-1">
                        <li>35% Bot Visits (normalized)</li>
                        <li>25% AI Bot Share</li>
                        <li>20% Category Importance</li>
                        <li>10% Sitemap Position</li>
                        <li>10% Enrichment Bonus</li>
                      </ul>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </TableHead>
              <TableHead className="text-center">Bot Visits</TableHead>
              <TableHead className="text-center">AI Share</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-[50px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredPriorities.map((priority, index) => (
              <TableRow key={priority.url} className={loading ? 'opacity-50' : ''}>
                <TableCell>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="font-mono text-sm truncate max-w-[400px]">
                          {formatUrl(priority.url)}
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="max-w-lg">
                        <p className="font-mono text-xs break-all">{priority.url}</p>
                        {priority.context.productName && (
                          <p className="text-xs mt-1">Product: {priority.context.productName}</p>
                        )}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </TableCell>

                <TableCell>
                  <div className="flex flex-col items-center gap-1">
                    <span className="font-bold text-lg">{priority.priorityScore.toFixed(1)}</span>
                    <Progress
                      value={priority.priorityScore}
                      className="h-1.5 w-16"
                    />
                  </div>
                </TableCell>

                <TableCell className="text-center">
                  <div className="flex items-center justify-center gap-1">
                    <Bot className="h-3 w-3 text-muted-foreground" />
                    <span>{priority.metrics.botVisits30d.toLocaleString()}</span>
                  </div>
                </TableCell>

                <TableCell className="text-center">
                  {priority.metrics.aiVisits30d > 0 ? (
                    <Badge variant="secondary" className="bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400">
                      {((priority.metrics.aiVisits30d / priority.metrics.botVisits30d) * 100).toFixed(0)}%
                    </Badge>
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </TableCell>

                <TableCell>
                  {priority.context.category ? (
                    <Badge variant="outline" className="truncate max-w-[120px]">
                      {priority.context.category}
                    </Badge>
                  ) : (
                    <span className="text-muted-foreground text-sm">-</span>
                  )}
                </TableCell>

                <TableCell>
                  {priority.context.isEnriched ? (
                    <Badge variant="default" className="bg-green-600">
                      <Eye className="h-3 w-3 mr-1" />
                      Enriched
                    </Badge>
                  ) : (
                    <Badge variant="secondary">
                      Pending
                    </Badge>
                  )}
                </TableCell>

                <TableCell>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleExcludeUrl(priority.url)}
                        >
                          <EyeOff className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Exclude from analysis</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </TableCell>
              </TableRow>
            ))}

            {filteredPriorities.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                  {priorities.length === 0
                    ? 'No URL priorities found. Click "Recalculate Priorities" to generate scores.'
                    : 'No URLs match the current filters. Try adjusting the include/exclude patterns.'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        {/* Pagination */}
        {priorities.length > 0 && (
          <div className="flex items-center justify-between mt-4">
            <div className="text-sm text-muted-foreground">
              Showing {filteredPriorities.length > 0 ? page * limit + 1 : 0} - {page * limit + filteredPriorities.length}
              {(includePattern || excludePattern) && ` (filtered from ${priorities.length})`}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0 || loading}
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => p + 1)}
                disabled={priorities.length < limit || loading}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
